// Utility functions for exporting data to CSV format

/**
 * Converts an array of objects to CSV format
 * @param {Array} data - Array of objects to convert
 * @param {Array} columns - Array of column definitions with {key, label, formatter}
 * @returns {string} CSV string
 */
export function arrayToCSV(data, columns) {
  if (!data || data.length === 0) {
    return ''
  }

  // Create header row
  const headers = columns.map(col => `"${col.label}"`).join(',')
  
  // Create data rows
  const rows = data.map(item => {
    return columns.map(col => {
      let value = item[col.key]
      
      // Apply formatter if provided
      if (col.formatter && typeof col.formatter === 'function') {
        value = col.formatter(value, item)
      }
      
      // Handle null/undefined values
      if (value === null || value === undefined) {
        value = ''
      }
      
      // Escape quotes and wrap in quotes
      const escapedValue = String(value).replace(/"/g, '""')
      return `"${escapedValue}"`
    }).join(',')
  })
  
  return [headers, ...rows].join('\n')
}

/**
 * Downloads CSV data as a file
 * @param {string} csvContent - CSV content string
 * @param {string} filename - Name of the file to download
 */
export function downloadCSV(csvContent, filename) {
  // Add UTF-8 BOM for proper character encoding (fixes m² display issue in Excel)
  const BOM = '\uFEFF'
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', filename)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }
}

/**
 * Formats currency values for CSV export
 * @param {number} value - Currency value
 * @returns {string} Formatted currency string
 */
export function formatCurrency(value) {
  if (!value || isNaN(value)) return ''
  return `$${Number(value).toLocaleString()}`
}

/**
 * Formats date values for CSV export
 * @param {string|Date} value - Date value
 * @returns {string} Formatted date string
 */
export function formatDate(value) {
  if (!value) return ''
  const date = new Date(value)
  return date.toLocaleDateString()
}

/**
 * Formats status values for CSV export
 * @param {string} value - Status value
 * @returns {string} Formatted status string
 */
export function formatStatus(value) {
  if (!value) return ''
  return value.toUpperCase()
}
